import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { type ArtifactKind, SocialOpportunitiesWidgetKind } from '@inference.engine/widgets/web';
import { EyeIcon, Trash2Icon } from 'lucide-react';
import { ArtifactKindBadge, ArtifactVersionBadge } from '@/entities/artifact';
import { getArtifactRowKey } from '@/entities/project-artifact';
import { AssetCard, type AssetCardAction } from '@/shared/ui/AssetCard';
import { getNoPermissionMessage } from '@/shared/ui';
import { resolveWidgetThumbnailComponent, WidgetThumbnail } from '@/shared/ui/WidgetThumbnail';
import { ARTIFACT_KIND_PALETTE } from '../../library.constants';
import { resolveArtifactResourceRoute } from '../../resolveArtifactResourceRoute';
import { useArtifactPreviewStore } from '../../store';
import { DateTimeCell } from '../DateTimeCell';
import type { Props } from './ArtifactCard.types';
import { useTranslation } from 'react-i18next';

/** BE types `artifactKind` as `string`; narrow to known palette keys, fall back to `misc`. */
function resolveKind(artifactKind: string): ArtifactKind {
  if (artifactKind in ARTIFACT_KIND_PALETTE) {
    return artifactKind as ArtifactKind;
  }
  return 'misc';
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function ArtifactCard(props: Props) {
  const { t } = useTranslation('projects');

  const { artifact, onDelete, deleteDisabled } = props;
  const navigate = useNavigate();
  const kind = resolveKind(artifact.artifactKind);
  const palette = ARTIFACT_KIND_PALETTE[kind];
  const KindIcon = palette.icon;

  const handleOpenPreview = () =>
    useArtifactPreviewStore
      .getState()
      .open(getArtifactRowKey(artifact), artifact.artifactName, kind, artifact.artifactKey ?? null);

  // Generated client widens nullable string fields to `unknown`; narrow at the boundary.
  const thumbnailUrl = stringOrNull(artifact.thumbnailUrl);
  const artifactKey = stringOrNull(artifact.artifactKey);
  const contextName = stringOrNull(artifact.originatingContextName);
  // image/video/file carry an upload/extracted poster; saved opportunities carry
  // the deep-research thumbnail (resolved to a signed `thumbnailUrl` server-side).
  // Saved opportunities aren't registered for live widget-thumbnail rendering
  // (their per-opportunity key isn't in WIDGET_THUMBNAIL_KEYS), so the poster is
  // the only thumbnail they have. Chat-flow opportunities keep their live widget
  // thumbnail, which wins the slot below.
  const hasMediaThumbnail =
    !!thumbnailUrl &&
    (kind === 'image' ||
      kind === 'video' ||
      kind === 'file' ||
      kind === SocialOpportunitiesWidgetKind);

  // Agent runs share a generic widget name so the chat context is the
  // user-meaningful primary; user-uploaded files keep the filename as primary
  // since it's already specific. Same shape as the picker's name cell.
  const isFile = artifact.creatorType === 'user';
  const primary = isFile ? artifact.artifactName : (contextName ?? artifact.artifactName);
  const secondary = isFile ? contextName : contextName != null ? artifact.artifactName : null;

  // The Library can hold several versions of the same artifact (identical title
  // + context), so the version is often the ONLY thing that tells two cards
  // apart — surface it as a `v{n}` badge in the tags row beside the kind badge,
  // matching the asset grid (shared with the picker via ArtifactVersionBadge).
  // File-kind artifacts (uploads AND agent
  // file-outputs) carry a unique filename, so they skip it.
  const versionBadge =
    kind !== 'file' && typeof artifact.artifactVersion === 'number' ? (
      <ArtifactVersionBadge version={artifact.artifactVersion} />
    ) : null;
  const description = secondary != null ? <span className="truncate">{secondary}</span> : undefined;

  const fallback = (
    <div className={`flex h-full w-full items-center justify-center ${palette.bg}`}>
      <KindIcon className={`size-12 ${palette.text}`} />
    </div>
  );

  // Video tiles render the server-extracted poster frame (a still PNG resolved
  // from `thumbnailAssetId`), so they share the plain `<img>` path with image
  // and file kinds rather than mounting a `<video>` element.
  // `hasMediaThumbnail` already restricts to image/video/file kinds, so any
  // matching row resolves to a still <img> (videos use their poster frame).
  const imageThumbnailUrl = hasMediaThumbnail ? thumbnailUrl : undefined;

  // Live widget preview for supported kinds. Only occupy the AssetCard
  // `thumbnail` slot (which wins over `thumbnailUrl`/`thumbnailFallback`) once
  // both a renderer and its bulk-loaded data exist — otherwise media posters
  // and the palette fallback keep their place. Unknown/unsupported kinds and
  // not-yet-loaded data leave `thumbnail` undefined → palette icon.
  const hasWidgetThumbnail =
    !!resolveWidgetThumbnailComponent(artifactKey) && props.widgetData != null;
  const widgetThumbnail = hasWidgetThumbnail ? (
    <WidgetThumbnail artifactKey={artifactKey} data={props.widgetData} fallback={fallback} />
  ) : undefined;

  // Saved tool resources (Content Test, …) carry a `${prefix}${id}` key that
  // resolves to their source view; other kinds have no standalone resource and
  // omit the View action.
  const resourceRoute = resolveArtifactResourceRoute(artifactKey);

  const actions = useMemo<AssetCardAction[] | undefined>(() => {
    const items: AssetCardAction[] = [];
    if (resourceRoute) {
      items.push({
        label: t('projectDetail.artifactCard.view'),
        icon: <EyeIcon className="size-4" />,
        onSelect: () => void navigate(resourceRoute),
      });
    }

    if (onDelete) {
      items.push({
        label: t('projectDetail.artifactCard.delete'),
        icon: <Trash2Icon className="size-4" />,
        onSelect: () => onDelete(artifact),
        destructive: true,
        disabled: deleteDisabled,
        disabledTooltip: getNoPermissionMessage(),
        // Divide the destructive action off from View when both are present.
        topDivider: items.length > 0,
      });
    }
    return items.length > 0 ? items : undefined;
  }, [resourceRoute, navigate, onDelete, artifact, deleteDisabled, t]);

  return (
    <AssetCard
      thumbnail={widgetThumbnail}
      thumbnailUrl={imageThumbnailUrl}
      thumbnailAlt={artifact.artifactName}
      thumbnailFallback={fallback}
      onClick={handleOpenPreview}
      title={primary}
      subtext={
        <DateTimeCell
          iso={artifact.attachedAt}
          label={t('projectDetail.artifactCard.addedToLibrary')}
        />
      }
      description={description}
      tags={
        <>
          <ArtifactKindBadge kind={kind} />
          {versionBadge}
        </>
      }
      actions={actions}
      actionsAriaLabel={t('projectDetail.artifactCard.artifactActions')}
    />
  );
}
